import stateApi from '@/api/stateApi';
import { CollectionOverviewSearch } from '@/components/collectionOverviewSearch';
import CollectionOverviewSettings from '@/components/collectionOverviewSettings';
import CollectionOverviewSort from '@/components/collectionOverviewSort';
import CollectionOverviewTree from '@/components/collectionOverviewTree';
import { LoadingIndicator } from '@/components/loadingIndicator';
import { type TreeItem } from '@/components/tree';
import { Group, Splitter } from '@mantine/core';
import { createFileRoute, Outlet, retainSearchParams, stripSearchParams } from '@tanstack/react-router';
import { INSPECT_DEFAULT_SEARCH, InspectSearchParams } from 'backend/src/lib/inspectSearchParams';
import type { Collection, CollectionStats } from 'lib/models/collection';
import type { Database } from 'lib/models/database';
import { type ReactNode } from 'react';

type Item = { db?: string; coll?: string };

export type CollectionOverviewItem = TreeItem & { label: ReactNode } & (
    | { type: 'root'; item: Item & { stats: CollectionStats } }
    | { type: 'db'; item: Item & Database }
    | { type: 'coll'; item: Item & Collection }
  );

export const Route = createFileRoute('/inspect')({
  component: RouteComponent,
  validateSearch: InspectSearchParams,
  search: {
    middlewares: [retainSearchParams(InspectSearchParams.keyof().options), stripSearchParams(INSPECT_DEFAULT_SEARCH)],
  },
});

function RouteComponent() {
  const ready = stateApi.state.useStore((x) => x.actual.ready);

  if (!ready) {
    return (
      <Group flex={1} justify="center" align="center">
        <LoadingIndicator />
      </Group>
    );
  }

  return (
    <Splitter
      handleColor="paper.9"
      css={{
        // a flex-basis would win over the height in the app shell's column layout, so the splitter
        // sizes itself explicitly and keeps its panes, not the page, scrolling
        flex: 'none',
        height: 'calc(100dvh - var(--app-shell-header-offset))',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Splitter.Pane
        defaultSize="400px"
        min="200px"
        css={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--mantine-color-body)',
        }}
      >
        <Group
          css={{
            padding: 'var(--mantine-spacing-xs)',
          }}
        >
          <CollectionOverviewSearch />
          <CollectionOverviewSort />
          <CollectionOverviewSettings />
        </Group>

        <CollectionOverviewTree />
      </Splitter.Pane>

      <Splitter.Pane
        defaultSize="100%"
        min="300px"
        css={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Outlet />
      </Splitter.Pane>
    </Splitter>
  );
}
